import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';

const authHeaders = () => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  return { Authorization: `Bearer ${token}` };
};

export interface CalendarConnectionStatus {
  sectorId: number;
  status: string;
  googleEmail: string | null;
  calendarId: string | null;
  calendarSummary: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  hasConnection: boolean;
  /** O Google recusou o acesso (permissão revogada ou expirada): precisa conectar de novo. */
  needsReconnect?: boolean;
}

export interface CalendarSettings {
  sectorId: number;
  timeZoneId: string;
  slotStepMinutes: number;
  minNoticeMinutes: number;
  bookingWindowDays: number;
  defaultAssigneeUserId: number | null;
  defaultAppointmentTypeId: number | null;
  configured: boolean;
}

export interface CalendarWorkingHours {
  dayOfWeek: number;
  openingTime: string;
  closingTime: string;
  isOpen: boolean;
}

export interface CalendarAppointmentType {
  id: number;
  name: string;
  durationMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  isDefault: boolean;
}

export interface CalendarEvent {
  id: number;
  title: string;
  description: string | null;
  location: string | null;
  startsAt: string;
  endsAt: string;
  status: string;
  occurrence?: string;
  outcome?: string;
  outcomeAt?: string | null;
  source: string;
  contactId: number | null;
  cardId: number | null;
  assigneeUserId: number | null;
  appointmentTypeId: number | null;
  googleSyncState: string;
}

export interface CalendarSlot {
  slotId: number | null;
  startsAt: string;
  endsAt: string;
  label: string;
}

export interface GoogleCalendarChoice {
  id: string;
  summary: string | null;
  primary: boolean;
  selected: boolean;
}

export interface UpsertCalendarEvent {
  title?: string;
  description?: string;
  location?: string;
  startsAt?: string;
  endsAt?: string;
  contactId?: number | null;
  appointmentTypeId?: number | null;
}

export const getCalendarConnectionStatus = async (sectorId: number): Promise<CalendarConnectionStatus> => {
  const response = await axios.get<CalendarConnectionStatus>(`${API_URL}/CalendarConnection/status/${sectorId}`, {
    headers: authHeaders(),
  });
  return response.data;
};

export const fetchCalendarAuthorizeUrlAndRedirect = async (sectorId: number): Promise<void> => {
  const response = await axios.get<{ url: string }>(
    `${API_URL}/CalendarConnection/oauth/authorize-url?sectorId=${sectorId}`,
    { headers: authHeaders() }
  );
  const url = response.data?.url;
  if (!url) throw new Error('No redirect URL received');
  window.location.href = url;
};

export const disconnectCalendar = async (sectorId: number): Promise<CalendarConnectionStatus> => {
  const response = await axios.post<CalendarConnectionStatus>(
    `${API_URL}/CalendarConnection/disconnect/${sectorId}`,
    {},
    { headers: authHeaders() }
  );
  return response.data;
};

export const listGoogleCalendars = async (sectorId: number): Promise<GoogleCalendarChoice[]> => {
  const response = await axios.get<GoogleCalendarChoice[]>(`${API_URL}/CalendarConnection/calendars/${sectorId}`, {
    headers: authHeaders(),
  });
  return response.data;
};

export const selectGoogleCalendar = async (sectorId: number, calendarId: string): Promise<CalendarConnectionStatus> => {
  const response = await axios.put<CalendarConnectionStatus>(
    `${API_URL}/CalendarConnection/calendar/${sectorId}`,
    { calendarId },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

export const getCalendarSettings = async (sectorId: number): Promise<CalendarSettings> => {
  const response = await axios.get<CalendarSettings>(`${API_URL}/calendar/settings`, {
    headers: authHeaders(),
    params: { sectorId },
  });
  return response.data;
};

export const saveCalendarSettings = async (settings: CalendarSettings): Promise<CalendarSettings> => {
  const response = await axios.put<CalendarSettings>(`${API_URL}/calendar/settings`, settings, {
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
  });
  return response.data;
};

export const getCalendarHours = async (sectorId: number): Promise<CalendarWorkingHours[]> => {
  const response = await axios.get<CalendarWorkingHours[]>(`${API_URL}/calendar/hours`, {
    headers: authHeaders(),
    params: { sectorId },
  });
  return response.data;
};

export const saveCalendarHours = async (sectorId: number, hours: CalendarWorkingHours[]): Promise<CalendarWorkingHours[]> => {
  const response = await axios.put<CalendarWorkingHours[]>(`${API_URL}/calendar/hours`, hours, {
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    params: { sectorId },
  });
  return response.data;
};

export const getCalendarTypes = async (sectorId: number): Promise<CalendarAppointmentType[]> => {
  const response = await axios.get<CalendarAppointmentType[]>(`${API_URL}/calendar/types`, {
    headers: authHeaders(),
    params: { sectorId },
  });
  return response.data;
};

export const saveCalendarType = async (sectorId: number, type: CalendarAppointmentType): Promise<CalendarAppointmentType> => {
  const url = type.id
    ? `${API_URL}/calendar/types/${type.id}`
    : `${API_URL}/calendar/types`;
  const response = type.id
    ? await axios.put<CalendarAppointmentType>(url, type, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        params: { sectorId },
      })
    : await axios.post<CalendarAppointmentType>(url, type, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        params: { sectorId },
      });
  return response.data;
};

export const deleteCalendarType = async (sectorId: number, id: number): Promise<void> => {
  await axios.delete(`${API_URL}/calendar/types/${id}`, {
    headers: authHeaders(),
    params: { sectorId },
  });
};

export const listCalendarEvents = async (sectorId: number, from: string, to: string): Promise<CalendarEvent[]> => {
  const response = await axios.get<CalendarEvent[]>(`${API_URL}/calendar/events`, {
    headers: authHeaders(),
    params: { sectorId, from, to },
  });
  return response.data;
};

export const createCalendarEvent = async (sectorId: number, body: UpsertCalendarEvent): Promise<CalendarEvent> => {
  const response = await axios.post<CalendarEvent>(`${API_URL}/calendar/events`, body, {
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    params: { sectorId },
  });
  return response.data;
};

export const updateCalendarEvent = async (sectorId: number, id: number, body: UpsertCalendarEvent): Promise<CalendarEvent> => {
  const response = await axios.put<CalendarEvent>(`${API_URL}/calendar/events/${id}`, body, {
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    params: { sectorId },
  });
  return response.data;
};

export const recordCalendarOutcome = async (
  sectorId: number,
  id: number,
  body: { occurrence: 'occurred' | 'no_show'; outcome: 'none' | 'no_decision' | 'closed' | 'lost' }
): Promise<CalendarEvent> => {
  const response = await axios.patch<CalendarEvent>(`${API_URL}/calendar/events/${id}/outcome`, body, {
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    params: { sectorId },
  });
  return response.data;
};

export const cancelCalendarEvent = async (sectorId: number, id: number): Promise<CalendarEvent> => {
  const response = await axios.post<CalendarEvent>(`${API_URL}/calendar/events/${id}/cancel`, {}, {
    headers: authHeaders(),
    params: { sectorId },
  });
  return response.data;
};

export const listCalendarSlots = async (
  sectorId: number,
  appointmentTypeId: number | null,
  from: string,
  to: string
): Promise<CalendarSlot[]> => {
  const response = await axios.get<CalendarSlot[]>(`${API_URL}/calendar/slots`, {
    headers: authHeaders(),
    params: { sectorId, appointmentTypeId: appointmentTypeId ?? undefined, from, to },
  });
  return response.data;
};
